import { InitialConfigType, LexicalComposer } from '@lexical/react/LexicalComposer';
import { LexicalErrorBoundary } from '@lexical/react/LexicalErrorBoundary';
import { HistoryPlugin } from '@lexical/react/LexicalHistoryPlugin';
import { LinkPlugin } from '@lexical/react/LexicalLinkPlugin';
import { ListPlugin } from '@lexical/react/LexicalListPlugin';
import { OnChangePlugin } from '@lexical/react/LexicalOnChangePlugin';
import { RichTextPlugin } from '@lexical/react/LexicalRichTextPlugin';
import { TabIndentationPlugin } from '@lexical/react/LexicalTabIndentationPlugin';
import { LexicalEditor } from 'lexical';
import { useMemo } from 'react';
import { OnBlurPlugin, PrefillPlugin } from '../plugins';
import ListMaxIndentLevelPlugin from '../plugins/ListMaxIndentLevelPlugin';
import MaxLengthPlugin from '../plugins/MaxLengthPlugin';
import ToolbarPlugin, { ToolbarPluginProps } from '../plugins/ToolbarPlugin';
import { editorTheme } from '../utils/features';
import nodes from './RichTextEditor/nodes';
import DisableFeaturesFunctionPlugin from './RichTextEditor/plugins/DisableFeaturesFunctionPlugin';
import EditableListenerPlugin from './RichTextEditor/plugins/EditableListenerPlugin';
import RichContentEditable, { RichContentEditableProps } from './RichTextEditor/RichContentEditable';
import useFilteredFeatures, { PossibleFeatureRequest } from './RichTextEditor/useFilteredFeatures';

export interface RichTextEditorProps extends Omit<InitialConfigType, 'namespace' | 'nodes' | 'onError'> {
    editable?: boolean;
    namespace?: string;
    /**
     * Placeholder shown if editor is empty, more example in link below
     * @link https://lexical.dev/docs/react/plugins#lexicalrichtextplugin
     */
    placeholder?: React.JSX.Element;
    readonly features?: PossibleFeatureRequest;
    contentEditableProps?: RichContentEditableProps;
    toolbarProps?: Omit<ToolbarPluginProps, 'features'>;
    initialHtml?: string;
    maxLength?: number;
    disableTabIndent?: boolean;

    loading?: boolean;
    loadingOverlay?: React.JSX.Element;

    onBlur?: (isDirty: boolean) => void;
    onChange?: Parameters<typeof OnChangePlugin>['0']['onChange'];
    /** handle error callback, otherwise error will be thrown by default */
    onError?: (err: Error, editor: LexicalEditor) => void;
}

const RichTextEditor = ({
    initialHtml,
    contentEditableProps,
    toolbarProps,
    placeholder,
    editorState,
    loading,
    loadingOverlay,
    onChange,
    onBlur,
    features: requestedFeatures,
    maxLength = Infinity,
    editable = true,
    onError = (error) => {
        throw error;
    },
    namespace = "TalentMesh's Rich Text Editor",
    theme = editorTheme,
    disableTabIndent = false,
}: RichTextEditorProps) => {
    const features = useFilteredFeatures(requestedFeatures);
    const withTabIndent = useMemo(
        () => !disableTabIndent && (features.includes('ol') || features.includes('ul')),
        [disableTabIndent, features],
    );

    return (
        <LexicalComposer initialConfig={{ namespace, onError, editable, editorState, nodes, theme }}>
            <HistoryPlugin />
            <ListPlugin />
            <LinkPlugin />
            {withTabIndent && (
                <>
                    <ListMaxIndentLevelPlugin />
                    <TabIndentationPlugin />
                </>
            )}
            {initialHtml && <PrefillPlugin initialHtml={initialHtml} />}
            {initialHtml && onBlur && <OnBlurPlugin initialHtml={initialHtml} onBlur={onBlur} />}
            {onChange && <OnChangePlugin onChange={onChange} ignoreSelectionChange ignoreHistoryMergeTagChange />}
            <ToolbarPlugin disabled={!editable} {...toolbarProps} features={features} />
            <EditableListenerPlugin editable={editable} />
            <DisableFeaturesFunctionPlugin supportedFeatures={features} />
            <MaxLengthPlugin maxLength={maxLength} />
            <RichTextPlugin
                placeholder={placeholder ?? null}
                ErrorBoundary={LexicalErrorBoundary}
                contentEditable={
                    loading && loadingOverlay ? loadingOverlay : <RichContentEditable {...contentEditableProps} />
                }
            />
        </LexicalComposer>
    );
};

export default RichTextEditor;
